import { useCallback, useEffect, useRef, useState } from "react";
import * as pdfjsLib from "pdfjs-dist";
import {
  ChevronLeft,
  ChevronRight,
  ZoomIn,
  ZoomOut,
  MessageSquare,
  X,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { Label } from "@/design/primitives";
import {
  docPdfUrl,
  fetchDocAnnotations,
  createDocAnnotation,
  updateDocAnnotation,
  replyDocAnnotation,
} from "@/lib/ccApi";
import "./docSurface.css";

// The worker file ships from the bundle's own build — craco.config.js copies
// pdfjs-dist's legacy worker to the build root and the backend's static route
// serves it same-origin. No CDN (DOC-P32-CONTRACT.md). If the worker cannot
// start, pdf.js itself degrades to the main-thread fake worker; the viewer's
// only error state is "the document could not be loaded at all".
pdfjsLib.GlobalWorkerOptions.workerSrc = "/pdf.worker.min.js";

// The PDF tab's engine (Phase A, P3.2 — DOC-P32-CONTRACT.md): the document is
// loaded with pdf.js from the backend's own /pdf endpoint and drawn page by
// page onto a canvas — one page shown at a time instead of the browser's
// foreign viewer in an iframe.
//
// P3.4 (DOC-ANNOT-CONTRACT.md): a threaded-comment layer over the same
// canvas. "Annotate" toggles box-draw mode (crosshair cursor); a drag draws
// a rubber-band rectangle (testid docs-annot-box, live during drag) whose
// release opens the composer (docs-annot-composer); saved notes render as
// outline boxes positioned by NORMALIZED rect: the drag's client coords ÷
// the canvas's client size → 0..1 stored on the backend; render multiplies
// back by the CURRENT canvas size, so boxes ride zoom and page changes for
// free. Clicking a saved box opens the thread panel (docs-annot-panel) with
// nested replies, a reply composer (docs-annot-reply), and resolve/reopen
// (docs-annot-resolve). A 401 from any annotation fetch degrades gracefully:
// the annotate affordances hide and the plain viewer keeps working.

const MIN_ZOOM = 0.5;
const MAX_ZOOM = 3.0;
const ZOOM_STEP = 0.25;

const clampZoom = (z) =>
  Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, Math.round(z * 1000) / 1000));

// Keep a normalized rect inside the page. Stored as x0,y0,x1,y1 in 0..1 —
// the backend rejects anything outside with a 422, and a drag that started
// (mouse leaves the canvas) must not produce an unusable box.
const clamp01 = (v) => Math.min(1, Math.max(0, v));

const normRect = (a, b) => {
  const x0 = clamp01(Math.min(a.x, b.x));
  const y0 = clamp01(Math.min(a.y, b.y));
  const x1 = clamp01(Math.max(a.x, b.x));
  const y1 = clamp01(Math.max(a.y, b.y));
  return [x0, y0, x1, y1];
};

const round6 = (v) => Math.round(v * 1e6) / 1e6;

// The rect rides back as TEXT (a JSON string) per the contract's schema, but
// tolerate an echoed array too — both shapes carry [x0,y0,x1,y1].
const parseRect = (v) => {
  if (Array.isArray(v)) return v.length === 4 ? v : null;
  if (typeof v !== "string") return null;
  try {
    const r = JSON.parse(v);
    return Array.isArray(r) && r.length === 4 ? r : null;
  } catch {
    return null;
  }
};

// Drag coords → normalized. The canvas is the page: its client box is the
// truth while the bitmap may be scaled by CSS; both live together here so
// the rect is zoom-independent by construction.
const pointInCanvas = (canvas, ev) => {
  const r = canvas.getBoundingClientRect();
  return {
    x: clamp01((ev.clientX - r.left) / (r.width || 1)),
    y: clamp01((ev.clientY - r.top) / (r.height || 1)),
  };
};

// One annotation's replies, flattening whatever nesting the backend chose —
// the contract promises one level deep; this keeps rendering honest anyway.
export const repliesOf = (row) =>
  (Array.isArray(row?.replies) ? row.replies : []);

const stampText = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return String(iso).slice(0, 16).replace("T", " ");
  return d.toLocaleString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
};

// ── the thread panel ──────────────────────────────────────────────────────
// The conversation for one annotation: body, nested replies, a reply
// composer, and the resolve/reopen door. The same panel serves the pdf
// boxes and the md highlights (DocsPage passes its own rows in).
export const ThreadPanel = ({ thread, onClose, onReply, onToggleResolve }) => {
  const [draft, setDraft] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const replies = repliesOf(thread);
  const resolved = thread.state === "resolved";

  const send = async () => {
    const body = draft.trim();
    if (!body) return;
    setBusy(true);
    setErr("");
    try {
      await onReply(thread, body);
      setDraft("");
    } catch (e) {
      setErr(e.message || "That didn't go through — try again or tell Kit.");
    } finally {
      setBusy(false);
    }
  };

  const toggle = async () => {
    setBusy(true);
    setErr("");
    try {
      await onToggleResolve(thread);
    } catch (e) {
      setErr(e.message || "That didn't go through — try again or tell Kit.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      data-testid="docs-annot-panel"
      className="w-[19rem] max-w-full shrink-0 border-l border-os-border bg-white"
      >
      <div className="flex items-start justify-between gap-2 border-b border-os-border px-3 py-2.5">
        <div className="min-w-0">
          <Label>Thread</Label>
          <p className="mt-0.5 font-mono text-[0.5625rem] uppercase tracking-[0.08em] text-os-ink-4">
            {resolved ? "resolved" : "open"} · {thread.author || "jeff"} ·{" "}
            {stampText(thread.created_at)}
          </p>
        </div>
        <button
          type="button"
          onClick={onClose}
          data-testid="docs-annot-panel-close"
          aria-label="Close thread"
          className="rounded-control p-1 text-os-ink-3 hover:bg-os-wash hover:text-os-ink"
        >
          <X className="h-3.5 w-3.5" strokeWidth={1.75} />
        </button>
      </div>
      <div className="max-h-[28rem] overflow-y-auto px-3 py-2.5">
        <p
          className="text-[0.8125rem] leading-[1.5] text-os-ink"
          data-testid="docs-annot-root-body"
        >
          {thread.body}
        </p>
        {thread.quote && (
          <p className="mt-1.5 border-l-2 border-os-bronze pl-2 text-[0.75rem] italic leading-[1.45] text-os-ink-2">
            “{thread.quote}”
          </p>
        )}
        {replies.length > 0 && (
          <ul className="mt-3 flex flex-col gap-2" data-testid="docs-annot-replies">
            {replies.map((r) => (
              <li key={r.id} data-testid="docs-annot-reply-row">
                <p className="text-[0.8125rem] leading-[1.5] text-os-ink">
                  {r.body}
                </p>
                <p className="mt-0.5 font-mono text-[0.5625rem] uppercase tracking-[0.08em] text-os-ink-4">
                  {r.author || "jeff"} · {stampText(r.created_at)}
                </p>
              </li>
            ))}
          </ul>
        )}
      </div>
      <div className="border-t border-os-border px-3 py-2.5">
        <textarea
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          placeholder="Write a reply…"
          data-testid="docs-annot-reply"
          rows={2}
          className="w-full rounded-control border border-os-border bg-white px-2.5 py-2 text-[0.8125rem] text-os-ink outline-none placeholder:text-os-ink-4 focus:border-os-ink-3"
        />
        {err && (
          <p className="mt-1 text-[0.75rem] text-os-red" data-testid="docs-annot-error">
            {err}
          </p>
        )}
        <div className="mt-2 flex items-center justify-between gap-2">
          <button
            type="button"
            onClick={toggle}
            disabled={busy}
            data-testid="docs-annot-resolve"
            className={cn(
              "rounded-control border px-2.5 py-1 text-[0.75rem]",
              resolved
                ? "border-os-border text-os-ink-3 hover:bg-os-wash"
                : "border-os-green text-os-green hover:bg-os-wash",
            )}
          >
            {resolved ? "Reopen" : "Resolve"}
          </button>
          <button
            type="button"
            onClick={send}
            disabled={busy || !draft.trim()}
            data-testid="docs-annot-reply-send"
            className="rounded-control border border-os-ink bg-os-wash px-2.5 py-1 text-[0.75rem] font-medium text-os-ink hover:bg-os-border disabled:opacity-50"
          >
            Reply
          </button>
        </div>
      </div>
    </div>
  );
};

const PdfViewer = ({ docId, title }) => {
  const [status, setStatus] = useState("loading"); // loading | ready | error
  const [pageCount, setPageCount] = useState(0);
  const [pageNum, setPageNum] = useState(1);
  const [zoom, setZoom] = useState(1);
  const canvasRef = useRef(null);
  // The pdf document lives outside React — engine state, not view state.
  const pdfRef = useRef(null);
  // Tokens: a stale async answer (slow page draw, slow load) must never
  // paint over the document that is on screen now.
  const renderTokenRef = useRef(0);

  // ── annotations (P3.4) ──
  // annotState: "hidden" (401 / never asked) | "idle" | "drawing" | "composer"
  const [annotMode, setAnnotMode] = useState(false);
  const [annotState, setAnnotState] = useState("idle");
  const [annots, setAnots] = useState([]); // full list, ALL pages
  const [annotsBlocked, setAnnotsBlocked] = useState(false); // 401 → hide affordances
  const [dragRect, setDragRect] = useState(null); // normalized [x0,y0,x1,y1] live
  // The press point while drawing — a ref, not state: only the finished rect
  // re-renders; the rubber-band uses dragRect, and the start point is needed
  // again only at mouseup (the composer's rect).
  const dragStartRef = useRef(null);
  const [openThreadId, setOpenThreadId] = useState(null);
  const [composerDraft, setComposerDraft] = useState("");
  const [annotBusy, setAnnotBusy] = useState(false);
  const [annotErr, setAnnotErr] = useState("");
  // The drawn page's bitmap size: the draw effect owns it, state mirrors it.
  // The overlay multiplies normalized rects by THIS — a state, not a live
  // canvas read — so a zoom change re-renders the boxes against the new
  // bitmap size the same draw that repaints the canvas (jsdom has no layout:
  // canvas.clientWidth is 0 and the bitmap canvas.width is the only truth).
  const [canvasSize, setCanvasSize] = useState(null);
  const drawTokenRef = useRef(0);

  // The annotations for this document: fetched ONCE per doc load (the full
  // thread list, every page), then filtered per page at render. A refresh
  // after save/resolve re-fetches the same list. A 401 (the annotations
  // store is not answering to this session) hides the layer entirely —
  // the viewer must never crash over it.
  // One load per document. The page count arrives before any drawing (pdf.js
  // needs numPages to answer "3 of 12"); pages are fetched and drawn only
  // when they are the one on screen.
  useEffect(() => {
    let live = true;
    const token = ++renderTokenRef.current;
    setStatus("loading");
    setPageCount(0);
    setPageNum(1);
    setZoom(1);
    pdfRef.current = null;

    const task = pdfjsLib.getDocument({ url: docPdfUrl(docId) });
    task.promise
      .then((pdf) => {
        if (!live || renderTokenRef.current !== token) return;
        pdfRef.current = pdf;
        setPageCount(pdf.numPages);
        setPageNum(1);
        setStatus("ready");
      })
      .catch(() => {
        if (!live || renderTokenRef.current !== token) return;
        setStatus("error");
      });

    return () => {
      live = false;
      task.destroy?.();
    };
  }, [docId]);

  // The annotations for this document: fetched per doc load (the full thread
  // list, every page), then filtered per page at render. A refresh after
  // save/resolve re-fetches the same list. A 401 (the annotations store is
  // not answering to this session) hides the layer entirely — the viewer
  // must never crash over it.
  useEffect(() => {
    setAnnotMode(false);
    setAnnotState("idle");
    setAnots([]);
    setAnnotsBlocked(false);
    setOpenThreadId(null);
    setDragRect(null);
    let live = true;
    fetchDocAnnotations(docId)
      .then((body) => {
        if (!live) return;
        const rows = Array.isArray(body?.annotations)
          ? body.annotations
          : Array.isArray(body)
            ? body
            : [];
        setAnots(rows);
        setAnnotsBlocked(false);
      })
      .catch((e) => {
        if (!live) return;
        if (e?.status === 401) {
          setAnnotsBlocked(true);
          setAnnotMode(false);
        }
        // every other failure is silent too: the pdf keeps working
        setAnots([]);
      });
    return () => {
      live = false;
    };
  }, [docId]);

  // A quiet refresh of the same list after a save/resolve — same 401 law.
  const refetchAnnots = useCallback(
    (docIdArg) => {
      fetchDocAnnotations(docIdArg)
        .then((body) => {
          const rows = Array.isArray(body?.annotations)
            ? body.annotations
            : Array.isArray(body)
              ? body
              : [];
          setAnots(rows);
          setAnnotsBlocked(false);
        })
        .catch(() => {
          /* silent: the boxes keep whatever they had */
        });
    },
    [],
  );

  // The draw: the current page at the current zoom, onto the canvas.
  useEffect(() => {
    if (status !== "ready") return undefined;
    let live = true;
    // A token per draw generation: a slower older draw must never set the
    // overlay size after a newer draw (or the unmount that follows it).
    const token = ++drawTokenRef.current;
    const drawing = (async () => {
      const canvas = canvasRef.current;
      const pdf = pdfRef.current;
      if (!canvas || !pdf) return;
      try {
        const page = await pdf.getPage(pageNum);
        if (!live || drawTokenRef.current !== token) return;
        const viewport = page.getViewport({ scale: zoom * 1.5 });
        const context = canvas.getContext("2d");
        canvas.width = Math.floor(viewport.width);
        canvas.height = Math.floor(viewport.height);
        await page.render({ canvasContext: context, viewport }).promise;
        if (!live || drawTokenRef.current !== token) return;
        setCanvasSize({ w: canvas.width, h: canvas.height });
      } catch {
        if (live && drawTokenRef.current === token) setStatus("error");
      }
    })();
    return () => {
      live = false;
    };
  }, [status, pageNum, zoom]);

  const pageAnnots = annots.filter((a) => Number(a.page) === pageNum);

  // ── the rubber-band drag ──
  // Pointer events on the canvas wrapper: down starts the box (annotate mode
  // only), move rubber-bands it, up opens the composer with the box live.
  // mouse* + touch (pointer events would need a jsdom shim anyway — the
  // mouse* trio is what both the DOM and jsdom give us for free).
  const onCanvasMouseDown = (e) => {
    if (!annotMode || annotsBlocked || status !== "ready") return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    e.preventDefault();
    const p = pointInCanvas(canvas, e);
    dragStartRef.current = p;
    setDragRect([p.x, p.y, p.x, p.y]);
    setAnnotState("drawing");
  };

  const onCanvasMouseMove = (e) => {
    if (annotState !== "drawing") return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    setDragRect(normRect(dragStartRef.current || { x: 0, y: 0 }, pointInCanvas(canvas, e)));
  };

  const onCanvasMouseUp = (e) => {
    if (annotState !== "drawing") return;
    const canvas = canvasRef.current;
    const start = dragStartRef.current;
    dragStartRef.current = null;
    if (!canvas || !start) {
      setAnnotState("idle");
      setDragRect(null);
      return;
    }
    const rect = normRect(start, pointInCanvas(canvas, e));
    setDragRect(rect);
    // A zero-area drag (a plain click while in annotate mode) is not a note.
    const area = (rect[2] - rect[0]) * (rect[3] - rect[1]);
    if (area <= 0.0004) {
      setAnnotState("idle");
      setDragRect(null);
      return;
    }
    setComposerDraft("");
    setAnnotErr("");
    setAnnotState("composer");
  };

  const saveAnnotation = async () => {
    const body = composerDraft.trim();
    if (!body || !dragRect) return;
    setAnnotBusy(true);
    setAnnotErr("");
    try {
      await createDocAnnotation(docId, {
        body,
        page: pageNum,
        rect: [
          round6(dragRect[0]),
          round6(dragRect[1]),
          round6(dragRect[2]),
          round6(dragRect[3]),
        ],
      });
      setAnnotState("idle");
      setDragRect(null);
      setComposerDraft("");
      setAnnotMode(false);
      refetchAnnots(docId);
    } catch (e) {
      setAnnotErr(e.message || "That didn't go through — try again or tell Kit.");
    } finally {
      setAnnotBusy(false);
    }
  };

  const cancelComposer = () => {
    setAnnotState("idle");
    setAnnotMode(false);
    setDragRect(null);
    setComposerDraft("");
    setAnnotErr("");
  };

  // The thread panel's doors — the row is replaced by the PATCH's own
  // response when available, refetched otherwise.
  const toggleResolve = async (thread) => {
    const nextState = thread.state === "resolved" ? "open" : "resolved";
    try {
      const updated = await updateDocAnnotation(thread.id, { state: nextState });
      setAnots((prev) =>
        prev.map((a) => {
          if (a.id !== thread.id) return a;
          const merged = { ...a, ...(updated && typeof updated === "object" ? updated : {}), id: a.id };
          if (!merged.replies && a.replies) merged.replies = a.replies;
          return merged;
        }),
      );
    } catch (e) {
      setAnnotErr(e.message || "That didn't go through — try again or tell Kit.");
    }
  };

  const sendReply = async (thread, body) => {
    const reply = await replyDocAnnotation(thread.id, body);
    setAnots((prev) =>
      prev.map((a) => {
        if (a.id !== thread.id) return a;
        const row = reply && typeof reply === "object" ? reply : null;
        const child = row && row.parent_id ? row : { id: `temp-${Date.now()}`, parent_id: thread.id, body, author: "jeff", created_at: new Date().toISOString() };
        return { ...a, replies: [...repliesOf(a), child] };
      }),
    );
  };

  const canPrev = status === "ready" && pageNum > 1;
  const canNext = status === "ready" && pageNum < pageCount;

  if (status === "error") {
    return (
      <div
        className="flex min-h-0 flex-1 flex-col items-center justify-center gap-2 px-6 py-10 text-center"
        data-testid="docs-pdf"
      >
        <p
          className="text-[0.875rem] text-os-ink-3"
          data-testid="docs-pdf-error"
        >
          This PDF could not be displayed
        </p>
      </div>
    );
  }

  const openThread = annots.find((a) => a.id === openThreadId) || null;

  return (
    <div className="flex min-h-0 flex-1 flex-col" data-testid="docs-pdf">
      <div className="flex items-center gap-2 border-b border-os-border px-4 py-2">
        <button
          type="button"
          onClick={() => setPageNum((p) => Math.max(1, p - 1))}
          disabled={!canPrev}
          data-testid="docs-pdf-prev"
          aria-label="Previous page"
          className={cn(
            "rounded-control border px-2 py-1 font-mono text-[0.625rem] uppercase tracking-[0.08em]",
            canPrev
              ? "border-os-border text-os-ink-3 hover:bg-os-wash"
              : "border-os-border text-os-ink-4 opacity-40",
          )}
        >
          <ChevronLeft className="h-3.5 w-3.5" strokeWidth={1.75} />
        </button>
        <button
          type="button"
          onClick={() => setPageNum((p) => Math.min(pageCount, p + 1))}
          disabled={!canNext}
          data-testid="docs-pdf-next"
          aria-label="Next page"
          className={cn(
            "rounded-control border px-2 py-1 font-mono text-[0.625rem] uppercase tracking-[0.08em]",
            canNext
              ? "border-os-border text-os-ink-3 hover:bg-os-wash"
              : "border-os-border text-os-ink-4 opacity-40",
          )}
        >
          <ChevronRight className="h-3.5 w-3.5" strokeWidth={1.75} />
        </button>
        <span
          className="font-mono text-[0.625rem] uppercase tracking-[0.08em] text-os-ink-3"
          data-testid="docs-pdf-page"
        >
          {status === "ready" ? `Page ${pageNum} of ${pageCount}` : ""}
        </span>
        <span className="flex-1" />
        {!annotsBlocked && (
          <button
            type="button"
            onClick={() => {
              setOpenThreadId(null);
              setAnnotMode((v) => !v);
              if (annotMode) setAnnotState("idle");
            }}
            data-testid="docs-annot-btn"
            aria-pressed={annotMode}
            className={cn(
              "inline-flex items-center gap-1.5 rounded-control border px-2.5 py-1 font-mono text-[0.625rem] uppercase tracking-[0.08em]",
              annotMode
                ? "border-os-ink-3 bg-os-wash text-os-ink"
                : "border-os-border text-os-ink-3 hover:bg-os-wash",
            )}
          >
            <MessageSquare className="h-3 w-3" strokeWidth={1.75} />
            Annotate
          </button>
        )}
        <button
          type="button"
          onClick={() => setZoom((z) => clampZoom(z - ZOOM_STEP))}
          disabled={status !== "ready" || zoom <= MIN_ZOOM}
          data-testid="docs-pdf-zoomout"
          aria-label="Zoom out"
          className={cn(
            "rounded-control border px-2 py-1 font-mono text-[0.625rem] uppercase tracking-[0.08em]",
            status === "ready" && zoom > MIN_ZOOM
              ? "border-os-border text-os-ink-3 hover:bg-os-wash"
              : "border-os-border text-os-ink-4 opacity-40",
          )}
        >
          <ZoomOut className="h-3.5 w-3.5" strokeWidth={1.75} />
        </button>
        <Label className="shrink-0">{Math.round(zoom * 100)}%</Label>
        <button
          type="button"
          onClick={() => setZoom((z) => clampZoom(z + ZOOM_STEP))}
          disabled={status !== "ready" || zoom >= MAX_ZOOM}
          data-testid="docs-pdf-zoomin"
          aria-label="Zoom in"
          className={cn(
            "rounded-control border px-2 py-1 font-mono text-[0.625rem] uppercase tracking-[0.08em]",
            status === "ready" && zoom < MAX_ZOOM
              ? "border-os-border text-os-ink-3 hover:bg-os-wash"
              : "border-os-border text-os-ink-4 opacity-40",
          )}
        >
          <ZoomIn className="h-3.5 w-3.5" strokeWidth={1.75} />
        </button>
      </div>

      <div className="flex min-h-0 flex-1">
        <div className="min-h-0 flex-1 overflow-auto">
          {status === "loading" && (
            <p
              className="px-4 py-6 text-[0.875rem] text-os-ink-3"
              data-testid="docs-pdf-loading"
            >
              Loading the PDF…
            </p>
          )}
          <div
            className="relative mx-auto w-fit"
            style={{ cursor: annotMode && !annotsBlocked ? "crosshair" : "auto" }}
            data-testid="docs-pdf-page-wrap"
            onMouseDown={onCanvasMouseDown}
            onMouseMove={onCanvasMouseMove}
            onMouseUp={onCanvasMouseUp}
          >
            <canvas
              ref={canvasRef}
              data-testid="docs-pdf-canvas"
              className={cn("block", status === "loading" && "hidden")}
            />
            {/* The overlay: the rubber-band during drag, then each saved
                annotation for THIS page, positioned by normalized rect ×
                the drawn bitmap size (canvasSize) — zoom-independent by
                construction and re-computed on every zoom change. */}
          {status === "ready" && (
            <div
              className="pointer-events-none absolute inset-0"
              data-testid="docs-annot-layer"
            >
              {dragRect && (
                <div
                  data-testid="docs-annot-box"
                  data-annot-state="drawing"
                  className="absolute border-2 border-dashed border-os-amber bg-os-amber/10"
                  style={{
                    left: `${dragRect[0] * (canvasSize ? canvasSize.w : 0)}px`,
                    top: `${dragRect[1] * (canvasSize ? canvasSize.h : 0)}px`,
                    width: `${(dragRect[2] - dragRect[0]) * (canvasSize ? canvasSize.w : 0)}px`,
                    height: `${(dragRect[3] - dragRect[1]) * (canvasSize ? canvasSize.h : 0)}px`,
                  }}
                />
              )}
              {pageAnnots.map((a) => {
                const rect = parseRect(a.rect);
                if (!rect) return null;
                if (!canvasSize) return null;
                const w = canvasSize.w;
                const h = canvasSize.h;
                return (
                  <div
                    key={a.id}
                    data-testid="docs-annot-box"
                    data-annot-id={a.id}
                    data-annot-state={a.state}
                    // The dot's hit area: pointer-events back on for just
                    // the box so a click reaches the panel-open handler
                    // while the layer itself stays click-through.
                    className="pointer-events-auto absolute border-2 border-os-amber"
                    style={{
                      left: `${rect[0] * w}px`,
                      top: `${rect[1] * h}px`,
                      width: `${(rect[2] - rect[0]) * w}px`,
                      height: `${(rect[3] - rect[1]) * h}px`,
                    }}
                    onClick={(e) => {
                      e.stopPropagation();
                      if (annotMode) return;
                      setOpenThreadId(a.id);
                    }}
                  />
                );
              })}
            </div>
          )}
          {/* The composer, anchored over the drawn box. */}
          {annotState === "composer" && dragRect && canvasSize && (
            <div
              data-testid="docs-annot-composer"
              className="absolute z-10 w-[16rem] rounded-card border border-os-border bg-white p-3 shadow-sm"
              style={{
                left: `${Math.min(dragRect[0], 0.75) * canvasSize.w}px`,
                top: `${Math.min(dragRect[3] * canvasSize.h + 8, 1e6)}px`,
              }}
            >
                <Label>Note on page {pageNum}</Label>
                <textarea
                  autoFocus
                  value={composerDraft}
                  onChange={(e) => setComposerDraft(e.target.value)}
                  placeholder="What's wrong in this box?"
                  data-testid="docs-annot-composer-text"
                  rows={3}
                  className="mt-2 w-full rounded-control border border-os-border bg-white px-2.5 py-2 text-[0.8125rem] text-os-ink outline-none placeholder:text-os-ink-4 focus:border-os-ink-3"
                />
                {annotErr && (
                  <p className="mt-1 text-[0.75rem] text-os-red">{annotErr}</p>
                )}
                <div className="mt-2 flex items-center justify-end gap-2">
                  <button
                    type="button"
                    onClick={cancelComposer}
                    data-testid="docs-annot-composer-cancel"
                    className="rounded-control border border-os-border bg-white px-2.5 py-1 text-[0.75rem] text-os-ink hover:bg-os-wash"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={saveAnnotation}
                    disabled={annotBusy || !composerDraft.trim()}
                    data-testid="docs-annot-composer-save"
                    className="rounded-control border border-os-ink bg-os-wash px-2.5 py-1 text-[0.75rem] font-medium text-os-ink hover:bg-os-border disabled:opacity-50"
                  >
                    Save
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
        {openThread && (
          <ThreadPanel
            thread={openThread}
            onClose={() => setOpenThreadId(null)}
            onReply={sendReply}
            onToggleResolve={toggleResolve}
          />
        )}
      </div>
    </div>
  );
};

export default PdfViewer;